/**
 * UI-3d Task 2 — DncDrawer.
 *
 * The CREATE-only drawer for a Do-Not-Call list entry (spec §3.4, §3.7),
 * hosted inside the shared `ResourceDrawer` (`width="standard"`).
 *
 * Backend contract (apps/api/src/routes/dnc.ts — `AddDncSchema`)
 * --------------------------------------------------------------
 *   POST /dnc   body: { phone, phoneE164, reason?, source?, expiresAt? }
 *     - phone      required, 1-20 chars
 *     - phoneE164  required, 1-20 chars
 *     - reason     optional, max 500 chars
 *     - source     optional enum: tenant | tcpa | ftc | gdpr | platform
 *     - expiresAt  optional ISO 8601 datetime string
 *
 * Per spec §3.4 the frontend sets `phone` and `phoneE164` to the SAME E.164
 * value — `phone` is a legacy non-E.164 storage slot, but since E.164 is
 * universal here they are identical.
 *
 * Immutability
 * ------------
 * DNC entries are immutable — there is no edit mode and no `entry` prop. The
 * drawer always creates a new entry.
 *
 * Save ownership
 * --------------
 * The props interface — `{ open, onClose, onSaved }` — has no `onSave`. The
 * drawer performs the API call itself, then calls `onSaved()` followed by
 * `onClose()`. On error a toast is shown and the drawer stays open.
 *
 * Remount per open
 * ----------------
 * `ZodForm` mount-initializes from `defaultValues` and does not re-sync.
 * `ResourceDrawer` returns `<></>` when `open` is false, so children are
 * fully unmounted on close. Keying `ZodForm` on the open state gives a fresh
 * empty form on every open with no double-mount.
 */
import { useRef, useState, type JSX } from 'react'
import { z } from 'zod'
import ResourceDrawer from '../ResourceDrawer.js'
import { ZodForm, FormText, FormTextarea, FormDropdown, FormCalendar } from '../../forms/index.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import { isValidE164 } from '../../../utils/e164.js'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface DncDrawerProps {
  open: boolean
  onClose: () => void
  /** Called after a successful save so the parent can refetch + close. */
  onSaved: () => void
}

const REASON_MAX = 500

/**
 * Source dropdown choices (spec §3.7). `tenant` is the default.
 *
 * Exported so sibling DNC components (e.g. `DncImportDialog`) reuse the single
 * 5-option list instead of duplicating it.
 */
export const SOURCE_OPTIONS = [
  { label: 'Tenant', value: 'tenant' },
  { label: 'TCPA', value: 'tcpa' },
  { label: 'FTC', value: 'ftc' },
  { label: 'GDPR', value: 'gdpr' },
  { label: 'Platform', value: 'platform' },
] as const

/** Memoised dropdown items — computed once at module load, not on every render. */
const SOURCE_DROPDOWN_OPTIONS = SOURCE_OPTIONS.map((o) => ({ label: o.label, value: o.value }))

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

const DncSchema = z.object({
  phoneE164: z
    .string()
    .trim()
    .min(1, 'Phone number is required')
    .refine(isValidE164, {
      message: 'Enter a valid phone number in E.164 format (e.g. +15551234567)',
    }),
  // Empty string is treated as "not provided"; max enforced for non-empty input.
  reason: z.string().max(REASON_MAX, `Reason must be ${REASON_MAX} characters or fewer`),
  source: z.enum(['tenant', 'tcpa', 'ftc', 'gdpr', 'platform']),
  // Optional future datetime. The backend is the source of truth for the
  // future-date constraint; this client-side refine just gives early feedback.
  expiresAt: z
    .date()
    .optional()
    .refine((d) => d === undefined || d.getTime() > Date.now(), {
      message: 'The expiry must be in the future',
    }),
})

interface DncFormValues {
  phoneE164: string
  reason: string
  source: 'tenant' | 'tcpa' | 'ftc' | 'gdpr' | 'platform'
  expiresAt?: Date
}

const DEFAULT_VALUES: DncFormValues = {
  phoneE164: '',
  reason: '',
  source: 'tenant',
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DncDrawer({ open, onClose, onSaved }: DncDrawerProps): JSX.Element {
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)

  // --- Save flow -----------------------------------------------------------

  async function handleSubmit(values: DncFormValues): Promise<void> {
    setSaving(true)
    try {
      // `phone` and `phoneE164` are the same E.164 value (see file header).
      // Optional fields are omitted entirely (not sent as `undefined`) so the
      // request body contains only keys the backend should act on.
      const body: Record<string, unknown> = {
        phone: values.phoneE164,
        phoneE164: values.phoneE164,
        source: values.source,
      }
      if (values.reason) body.reason = values.reason
      if (values.expiresAt) body.expiresAt = values.expiresAt.toISOString()
      await api.post('/dnc', body)
      toast.success('DNC entry added')
      onSaved()
      onClose()
    } catch (err) {
      // Keep the drawer open on failure; surface a readable error.
      toast.error(err instanceof Error ? err.message : 'Failed to add DNC entry.')
    } finally {
      setSaving(false)
    }
  }

  // --- Render --------------------------------------------------------------

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      formRef={formRef}
      saving={saving}
      width="standard"
      title="Add to DNC List"
    >
      <ZodForm
        key={open ? 'open' : 'closed'}
        schema={DncSchema}
        defaultValues={DEFAULT_VALUES}
        onSubmit={(data) => handleSubmit(data as DncFormValues)}
        formRef={formRef}
      >
        <FormText
          name="phoneE164"
          label="Phone"
          type="tel"
          required
          autoFocus
          placeholder="+15551234567"
          helpText="E.164 format — a leading + and country code."
        />

        <FormTextarea
          name="reason"
          label="Reason"
          rows={3}
          placeholder="Optional — why this number is on the DNC list"
          helpText={`Optional — ${REASON_MAX} characters or fewer.`}
        />

        <FormDropdown
          name="source"
          label="Source"
          options={SOURCE_DROPDOWN_OPTIONS}
          helpText="Where this DNC entry originated."
        />

        <FormCalendar
          name="expiresAt"
          label="Expiry"
          showTime
          hourFormat="24"
          showIcon
          helpText="Optional — leave empty for a permanent entry."
        />
      </ZodForm>
    </ResourceDrawer>
  )
}
